import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import styled, { keyframes } from 'styled-components';
import { Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { DefaultButtonStyled } from '@/components/atom/buttons/DefaultButton';
import HandHeldTabletWriting from '@/components/atom/icon/HandHeldTabletWriting';
import Trophy from '@/components/atom/icon/Trophy';
import { zIndex } from '@/constants/zIndex';
import { useGetCommunityUserQuery } from '@/hooks/community/mobile/community.gql.impl';
import useAuthModal from '@/hooks/useAuthModal';
import useCustomRouter from '@/hooks/useCustomRouter';
import useModal from '@/hooks/useModal';
import { tokenState } from '@/recoil/atoms/common/auth';
import { communityUserService } from '@/services/community';
import { BoardCategory } from '@/types/bff/gql-schema';
import { ROUTES } from '@/types/common';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';
import { isBetweenDate } from '@/utils/date';
import { mobileLogger } from '@/utils/logger';
import { isWebView } from '@/utils/mobile/RNPlugins';

interface Props {
  pageName: string;
  selectedCategory?: BoardCategory;
  width?: number;
}

interface Banner {
  Icon: () => JSX.Element;
  title: string;
  headerTitle: string;
  uri: string;
  startAt: string;
  endAt: string;
}
const WriteFixedBottomSheet = ({ pageName, selectedCategory, width = 768 }: Props) => {
  const token = useRecoilValue(tokenState);
  const { setShowAuthModal } = useAuthModal();
  const { moveToPage, router } = useCustomRouter();
  const { showModal, closeModal } = useModal();
  const [activeBanners, setActiveBanners] = useState<Array<Banner>>([]);

  // 유저 정보 가져오기
  const { data: communityUser } = useGetCommunityUserQuery(
    {},
    {
      enabled: !!token,
      select: (data) => communityUserService(data),
      refetchOnWindowFocus: true,
    },
  );

  const banners: Array<Banner> = useMemo(
    () => [
      {
        Icon: () => <HandHeldTabletWriting />,
        title: '커뮤니티 이용가이드를 읽어보세요.',
        headerTitle: '커뮤니티 이용가이드',
        uri: 'https://company.doctornow.co.kr/communityguide',
        startAt: '2023-06-07 15:00',
        endAt: '2025-06-07 15:00',
      },
      {
        Icon: () => <Trophy />,
        title: '커뮤니티 우수 회원 발표!',
        headerTitle: '우수 회원 발표',
        uri: 'https://company.doctornow.co.kr/community_prize_4/',
        startAt: '2023-09-01 15:00',
        endAt: '2023-09-05 15:00',
      },
    ],
    [],
  );

  const onClickWrite = () => {
    if (!token) return setShowAuthModal(true);
    if (communityUser?.isBanned) {
      showModal({
        type: MODAL_TYPE.ALERT,
        props: {
          title: '커뮤니티 정책을 위반했어요.',
          message: `제한 기간 동안 게시글과 댓글을 쓸 수 없어요.\n(제한 기간 : ${communityUser.formattedBannedDate})`,
          buttons: [
            {
              type: MODAL_BUTTON_TYPE.DEFAULT,
              label: '확인',
              onClick: closeModal,
            },
          ],
        },
      });
    } else {
      mobileLogger({
        logger: {
          eventName: 'comm_write_bottom_sheet_click',
          pageName,
          payload: router.query as { [key: string]: string | number },
        },
      });

      moveToPage({
        webLink: `${ROUTES.COMMUNITY.WRITE.path}${
          selectedCategory
            ? `?categoryId=${selectedCategory.boardCategoryId}&categoryName=${selectedCategory.name}`
            : ''
        }`,
        mobileScreenName: ROUTES.COMMUNITY.WRITE.appScreenName,
        params: {
          categoryId: selectedCategory?.boardCategoryId || '',
          categoryName: selectedCategory?.name || '',
        },
      });
    }
  };

  const onClickContent = (uri: string, key: number) => {
    mobileLogger({
      logger: {
        eventName: 'comm_bottom_sheet_content_click',
        pageName,
        payload: { link: uri },
      },
    });

    moveToPage({
      webLink: banners[key as number].uri,
      mobileScreenName: ROUTES.COMMON.PURE_WEBVIEW.appScreenName,
      params: {
        headerTitle: banners[key as number].headerTitle,
        uri: banners[key as number].uri,
      },
    });
  };

  useEffect(() => {
    setActiveBanners(
      banners.filter((banner) =>
        isBetweenDate({
          startAt: banner.startAt,
          endAt: banner.endAt,
          unitType: 'minutes',
        }),
      ),
    );
  }, [banners]);

  return (
    <Wrapper id={'FixedBottomSheet'} width={width} isWebView={isWebView()}>
      <Body>
        <Content>
          <Gradient />
          <SwiperContainer
            slidesPerView={1}
            direction={'vertical'}
            modules={[Autoplay]}
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            loop={activeBanners.length > 1}
          >
            {activeBanners.map((event, key) => (
              <SwiperSlide key={event.uri} onClick={() => onClickContent(event.uri, key)}>
                <BannerItem>
                  <event.Icon />
                  {event.title}
                </BannerItem>
              </SwiperSlide>
            ))}
          </SwiperContainer>
          <Gradient bottom />
        </Content>
        <WriteButton onClick={onClickWrite}>글쓰기</WriteButton>
      </Body>
    </Wrapper>
  );
};

export default WriteFixedBottomSheet;

const BottomUp = keyframes`
  0% {
    transform: translateY(100%);
  }
  100% {
    transform: translateY(0);
  }
`;

const Wrapper = styled.div<{ width: number; isWebView: boolean }>`
  position: fixed;
  width: ${({ width, isWebView }) => (isWebView ? '100%' : `${width}px`)};
  display: ${({ width }) => (width > 0 ? 'block' : 'none')};
  max-width: inherit;
  background: white;
  box-shadow: 0 -2px 8px rgb(0 0 0 / 8%);
  border-radius: 16px 16px 0 0;
  bottom: 0;
  animation: ${BottomUp} 0.3s;
  z-index: ${zIndex.BottomSticky};
`;

const Body = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 16px;
  align-items: center;
  margin: 0 auto;
  max-width: inherit;
  ${({ theme }) => `
    ${theme.typography.UI14SB};
    color: ${theme.color.gray500};
  `}
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  position: relative;

  svg {
    margin-right: 12px;
  }
`;

const WriteButton = styled(DefaultButtonStyled)`
  height: 40px;
  padding: 0 21.5px;
  border-radius: 8px;

  ${({ theme }) => `
    ${theme.typography.UI14SB};
    color: ${theme.color.white};
    background: ${theme.color.primary500};
    &: active {
      background: ${theme.color.primary700};
    }
  `}
`;

const SwiperContainer = styled(Swiper)`
  height: 68px;
  width: 100%;
`;

const BannerItem = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  align-items: center;
`;

const Gradient = styled.div<{ bottom?: boolean }>`
  position: absolute;
  height: 16px;
  width: 100%;
  z-index: 2;
  ${({ bottom }) =>
    bottom
      ? `
        background: linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%); 
        top: 0`
      : `
        background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 100%); 
        bottom: 0`};
`;
